import { useEffect, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SKIP_TAGS = new Set(['INPUT', 'TEXTAREA', 'SCRIPT', 'STYLE', 'SVG', 'PATH']);

function collectTextNodes(root) {
  const out = [];
  const walk = (el) => {
    for (const n of el.childNodes) {
      if (n.nodeType === 3) {
        if (n.nodeValue && n.nodeValue.trim()) out.push(n);
      } else if (n.nodeType === 1) {
        if (SKIP_TAGS.has(n.tagName)) continue;
        if (n.getAttribute && n.getAttribute('data-no-translate') != null) continue;
        walk(n);
      }
    }
  };
  walk(root);
  return out;
}

/**
 * Live EN/ES form translator. On 'es' it collects visible label/heading/button/option
 * text (never input/textarea values), batch-translates via the shared cache and swaps
 * the text in place; a MutationObserver re-applies after React re-renders. On 'en' it
 * restores the captured originals. Entered data stays in inputs and is never touched.
 */
export const AutoTranslate = ({ lang, children, className }) => {
  const ref = useRef(null);
  const mapRef = useRef({});
  const obsRef = useRef(null);
  const applyingRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    let cancelled = false;

    const applyPlaceholders = (map, restore) => {
      root.querySelectorAll('input[placeholder],textarea[placeholder]').forEach((el) => {
        if (el.__origPh == null) el.__origPh = el.getAttribute('placeholder') || '';
        const o = el.__origPh;
        if (restore) { if (o != null) el.setAttribute('placeholder', o); return; }
        if (o && map[o.trim()]) el.setAttribute('placeholder', map[o.trim()]);
      });
    };

    const restore = () => {
      collectTextNodes(root).forEach((n) => { if (n.__orig != null) n.nodeValue = n.__orig; });
      applyPlaceholders({}, true);
    };

    const applyNow = () => {
      const map = mapRef.current;
      applyingRef.current = true;
      collectTextNodes(root).forEach((n) => {
        if (n.__orig == null) n.__orig = n.nodeValue;
        const o = (n.__orig || '').trim();
        if (o && map[o] != null) n.nodeValue = n.__orig.replace(o, map[o]);
      });
      applyPlaceholders(map, false);
      applyingRef.current = false;
    };

    const translate = async () => {
      const nodes = collectTextNodes(root);
      const strings = new Set();
      nodes.forEach((n) => { if (n.__orig == null) n.__orig = n.nodeValue; strings.add(n.__orig.trim()); });
      root.querySelectorAll('input[placeholder],textarea[placeholder]').forEach((el) => {
        const p = el.getAttribute('placeholder'); if (p && p.trim()) strings.add(p.trim());
      });
      applyNow(); // show whatever is already cached in memory right away
      const missing = [...strings].filter((s) => s && !(s in mapRef.current));
      const token = localStorage.getItem('tfs_token');
      // Translate in small chunks and apply after each so cached strings appear fast
      // and the form fills in progressively rather than waiting on one huge request.
      for (let i = 0; i < missing.length; i += 12) {
        if (cancelled) return;
        const part = missing.slice(i, i + 12);
        try {
          const r = await axios.post(`${API}/operations/i18n/ui`, { texts: part, target: 'es' },
            { headers: { Authorization: `Bearer ${token}` } });
          mapRef.current = { ...mapRef.current, ...(r.data.map || {}) };
        } catch (e) { /* leave these English on failure */ }
        if (cancelled) return;
        applyNow();
      }
    };

    if (obsRef.current) { obsRef.current.disconnect(); obsRef.current = null; }

    if (lang === 'es') {
      translate();
      const obs = new MutationObserver(() => {
        if (applyingRef.current || cancelled) return;
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => { if (!cancelled) translate(); }, 120);
      });
      obs.observe(root, { childList: true, subtree: true, characterData: true });
      obsRef.current = obs;
    } else {
      restore();
    }

    return () => {
      cancelled = true;
      clearTimeout(timerRef.current);
      if (obsRef.current) { obsRef.current.disconnect(); obsRef.current = null; }
    };
  }, [lang]);

  return <div ref={ref} className={className}>{children}</div>;
};

export default AutoTranslate;
